/**
 * DecisionFlow — TemplateDiffView
 * ui/src/components/tenant-admin/TemplateDiffView.jsx
 *
 * S694 — Template Difference Engine (Bucket 3)
 * Spec authority: comp_policies_spec_v2_11.md S26.4
 * AD-68: one component, two render modes — never two implementations.
 * NN-36: two-actor SoD — delta_acknowledged_by ≠ instantiated_by (enforced at DB level).
 *
 * Props:
 *   mode             — 'review' | 'acknowledge'
 *   deltaData        — object returned by GET /api/v1/template-library/deltas/by-plan/{plan_id}
 *                      { delta_id, template_name, template_code, delta_payload[],
 *                        has_delta, delta_count, delta_acknowledged,
 *                        delta_acknowledged_by, delta_acknowledged_at }
 *   loading          — bool — caller controls fetch state
 *   error            — string | null — caller surfaces fetch errors
 *   onAcknowledgedChange — (bool) => void — mode='acknowledge' only
 *                          called when checkbox state changes; parent gates Approve button
 *
 * mode='review' (CA-1):
 *   Read-only. Informational. Shown in PlanDetailView ReviewScreen for PATH A plans.
 *   No state. No interaction. Zero props required beyond deltaData.
 *
 * mode='acknowledge' (CA-2):
 *   Checkbox: "I acknowledge these deviations from the certified template"
 *   Approve button disabled in parent until onAcknowledgedChange(true) fires.
 *   Owns checkboxAcked state only — no network calls (parent owns acknowledge POST).
 *
 * delta_payload item shape (from _compute_delta in template_deployment_router.py):
 *   { field, template_value, plan_value, change_type: 'changed'|'added'|'removed' }
 *   Note: spec S26.4 calls this field 'status' — stored as 'change_type'. UI reads
 *   change_type as stored; label difference only, no backend change required.
 *
 * Styling: rgb(var(--color-*)) tokens only. No hardcoded colors. IBM Plex Sans.
 */

import React, { useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') return val.toLocaleString();
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
}

// Human-readable field labels — keyed on the field name stored in delta_payload.
// Extend as new fields are captured in _compute_delta.
const FIELD_LABEL = {
  draw_amount:          'Draw & Guarantee Amount',
  kicker_threshold:     'Kicker / Multiplier Threshold',
  base_pct:             'Base %',
  variable_pct:         'Variable %',
  arr_weight:           'ARR Weight',
  mbo_weight:           'MBO Weight',
  nps_weight:           'NPS Weight',
  adoption_weight:      'Adoption Weight',
  ote_min:              'OTE Minimum',
  ote_max:              'OTE Maximum',
  ote_midpoint:         'OTE Midpoint',
  clawback_rate:        'Clawback Rate',
  clawback_period_days: 'Clawback Period (Days)',
  accelerator_rate:     'Accelerator Rate',
  cap_amount:           'Cap Amount',
  spiff_amount:         'SPIFF Amount',
  transaction_rate:     'Transaction Rate',
};

function fieldLabel(field) {
  return FIELD_LABEL[field] ?? field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const CHANGE_TYPE_STYLE = {
  changed: {
    badge:     'bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 text-[rgb(var(--color-warning))]',
    label:     'Changed',
    rowBorder: 'border-l-2 border-l-[rgb(var(--color-warning))]',
  },
  added: {
    badge:     'bg-[rgb(var(--color-info))]/10 border border-[rgb(var(--color-info))]/30 text-[rgb(var(--color-info))]',
    label:     'Added',
    rowBorder: 'border-l-2 border-l-[rgb(var(--color-info))]',
  },
  removed: {
    badge:     'bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))]',
    label:     'Removed',
    rowBorder: 'border-l-2 border-l-[rgb(var(--color-danger))]',
  },
};

// ---------------------------------------------------------------------------
// NoDeltaBanner — shown when delta_payload is empty (plan matches template exactly)
// ---------------------------------------------------------------------------

function NoDeltaBanner({ templateName, mode }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
      <svg
        className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]"
        fill="none" viewBox="0 0 24 24" stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p className="text-tok-sm font-medium text-[rgb(var(--color-success))]">
          No deviations from certified template
        </p>
        <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-success))]/80">
          {templateName
            ? `This plan matches "${templateName}" exactly as deployed.`
            : 'This plan matches the certified template exactly as deployed.'}
          {mode === 'acknowledge' && ' No acknowledgment is required.'}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeltaTable — renders the field-level diff rows
// ---------------------------------------------------------------------------

function DeltaTable({ items }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[rgb(var(--color-border))]">
      <table className="w-full border-collapse text-tok-sm">
        <thead>
          <tr className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
            <th className="px-4 py-2.5 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Field
            </th>
            <th className="px-4 py-2.5 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Template Value
            </th>
            <th className="px-4 py-2.5 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Plan Value
            </th>
            <th className="px-4 py-2.5 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Change
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[rgb(var(--color-border))]">
          {items.map((item, idx) => {
            const ct = item.change_type ?? 'changed';
            const style = CHANGE_TYPE_STYLE[ct] ?? CHANGE_TYPE_STYLE.changed;
            return (
              <tr
                key={`${item.field}-${idx}`}
                className={`bg-[rgb(var(--color-surface))] ${style.rowBorder}`}
              >
                <td className="px-4 py-3 font-medium text-[rgb(var(--color-text))]">
                  {fieldLabel(item.field)}
                  <span className="ml-2 font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
                    {item.field}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {ct === 'added' ? (
                    <span className="italic text-[rgb(var(--color-text-muted))]">not in template</span>
                  ) : (
                    fmtValue(item.template_value)
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-tok-xs text-[rgb(var(--color-text))]">
                  {ct === 'removed' ? (
                    <span className="italic text-[rgb(var(--color-text-muted))]">removed in plan</span>
                  ) : (
                    fmtValue(item.plan_value)
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${style.badge}`}>
                    {style.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemplateDiffView — main export (AD-68: one component, two render modes)
// ---------------------------------------------------------------------------

export default function TemplateDiffView({
  mode,
  deltaData,
  loading = false,
  error = null,
  onAcknowledgedChange,
}) {
  // mode='acknowledge' only — checkbox state
  const [checkboxAcked, setCheckboxAcked] = useState(false);

  function handleCheckboxChange(e) {
    const checked = e.target.checked;
    setCheckboxAcked(checked);
    if (onAcknowledgedChange) onAcknowledgedChange(checked);
  }

  // ── Loading state ──
  if (loading) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-4 animate-pulse">
        <div className="h-4 w-48 rounded bg-[rgb(var(--color-surface-muted))] mb-3" />
        <div className="h-24 rounded-lg bg-[rgb(var(--color-surface-muted))]" />
      </div>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-4">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] mb-2">
          Template Comparison
        </p>
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Template comparison unavailable — {error}
        </div>
      </div>
    );
  }

  // ── No data yet (deltaData not loaded) ──
  if (!deltaData) return null;

  const items        = deltaData.delta_payload ?? [];
  const hasDelta     = items.length > 0;
  const templateName = deltaData.template_name ?? deltaData.template_code ?? null;
  const alreadyAcked = deltaData.delta_acknowledged ?? false;

  // ── Shared header ──
  const header = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          {mode === 'acknowledge' ? 'Template Deviation Review' : 'Template Comparison'}
        </p>
        {templateName && (
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            · {templateName}
          </span>
        )}
      </div>
      {hasDelta && (
        <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">
          {items.length} deviation{items.length !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );

  // ============================================================
  // mode='review' (CA-1) — read-only, informational
  // No state. No interaction.
  // ============================================================
  if (mode === 'review') {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))]">
        <div className="px-4 py-3">
          {header}
          {!hasDelta && (
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Informational only — no acknowledgment required at this step.
            </p>
          )}
        </div>

        <div className="px-4 py-4">
          {!hasDelta ? (
            <NoDeltaBanner templateName={templateName} mode="review" />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="rounded-lg border border-[rgb(var(--color-info))]/20 bg-[rgb(var(--color-info))]/5 px-3 py-2">
                <p className="text-tok-xs text-[rgb(var(--color-info))]">
                  This plan deviates from the certified template in {items.length} field{items.length !== 1 ? 's' : ''}.
                  A second Compensation Administrator will review and acknowledge these deviations
                  in the Approvals tab before approving the plan components.
                </p>
              </div>
              <DeltaTable items={items} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // mode='acknowledge' (CA-2) — checkbox gate
  // Owns checkboxAcked state. Parent gates Approve button via onAcknowledgedChange.
  // ============================================================
  if (mode === 'acknowledge') {
    // Already acknowledged — show confirmation, no checkbox needed
    if (alreadyAcked) {
      return (
        <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <svg className="h-4 w-4 text-[rgb(var(--color-success))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-tok-sm font-medium text-[rgb(var(--color-success))]">
              Template deviations acknowledged
            </p>
          </div>
          {deltaData.delta_acknowledged_at && (
            <p className="text-tok-xs text-[rgb(var(--color-success))]/80">
              Acknowledged {new Date(deltaData.delta_acknowledged_at).toLocaleString()}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))]">
        <div className="px-4 py-3">
          {header}
        </div>

        <div className="px-4 py-4">
          {!hasDelta ? (
            <NoDeltaBanner templateName={templateName} mode="acknowledge" />
          ) : (
            <div className="flex flex-col gap-3">
              {/* Governance context banner */}
              <div className="rounded-lg border border-[rgb(var(--color-warning))]/20 bg-[rgb(var(--color-warning))]/5 px-3 py-2">
                <p className="text-tok-xs font-medium text-[rgb(var(--color-warning))]">
                  This plan was modified after deployment from the certified template.
                  Review each deviation below before approving.
                </p>
              </div>

              <DeltaTable items={items} />

              {/* Acknowledgment checkbox — must be checked before parent enables Approve */}
              <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 hover:bg-[rgb(var(--color-surface))] transition-colors">
                <input
                  type="checkbox"
                  checked={checkboxAcked}
                  onChange={handleCheckboxChange}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--color-primary))] cursor-pointer"
                />
                <span className="text-tok-sm text-[rgb(var(--color-text))] leading-relaxed">
                  I acknowledge these deviations from the certified template
                </span>
              </label>

              {!checkboxAcked && (
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  You must acknowledge the deviations above before the Approve button is enabled.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Guard — invalid mode prop
  return null;
}
